/**
 * Pillars section on Home
 * Clearly presents the two service pillars with quick links to subservices
 */

import React from 'react'
import { Link } from 'react-router'
import { Button } from '../../components/ui/button'
import { TrendingUp, Target, ArrowRight, Users, Globe, FileText, Mic, Tv, Radio, Eye } from 'lucide-react'
import SectionHeader from '../common/SectionHeader'

/** Link item for a subservice */
interface SubLink {
  /** Display text for the subservice */
  label: string
  /** Route path to the subservice page */
  to: string
}

/** Card props for each pillar */
interface PillarCardProps {
  /** Icon element at the top of the card */
  icon: React.ReactNode
  /** Title of the pillar */
  title: string
  /** Supporting description */
  description: string
  /** CTA label */
  cta: string
  /** CTA route */
  ctaTo: string
  /** List of subservice links */
  links: SubLink[]
  /** Accent color classes used in the icon ring and hover */
  accent: { ring: string; icon: string; hover: string; border: string }
}

/** Single card representing a pillar */
function PillarCard({
  icon,
  title,
  description,
  cta,
  ctaTo,
  links,
  accent,
}: PillarCardProps): JSX.Element {
  return (
    <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100">
      <div className="flex items-center mb-6">
        <div className={`p-3 rounded-lg mr-4 ${accent.ring}`}>
          <div className={`${accent.icon}`}>{icon}</div>
        </div>
        <h3 className="text-2xl font-bold text-gray-900">{title}</h3>
      </div>

      <p className="text-gray-600 mb-6 leading-relaxed">{description}</p>

      <div className="space-y-3 mb-6">
        {links.map((l) => (
          <Link
            key={l.to}
            to={l.to}
            className={`flex items-center justify-between p-4 bg-gray-50 hover:${accent.hover} rounded-lg transition-colors`}
          >
            <span className="font-medium text-gray-900">{l.label}</span>
            <ArrowRight className="h-5 w-5 text-orange-600" />
          </Link>
        ))}
      </div>

      <Button asChild className="w-full bg-orange-600 hover:bg-orange-700">
        <Link to={ctaTo}>{cta}</Link>
      </Button>
    </div>
  )
}

/** Two-pillars section that appears under the hero on Home */
export default function Pillars(): JSX.Element {
  return (
    <section className="py-16 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHeader
          eyebrow="We don't just place ads — we create impact."
          title="Two Pillars, One Integrated Team"
          subtitle="From data-driven digital campaigns to influencer collaborations and prime-time TV media buys, our integrated strategies connect your brand with millions of Iraqis every day. With deep cultural understanding and unmatched industry partnerships, Edicoo makes sure your message is seen, heard, and remembered."
        />

        <div className="mt-12 grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Digital Marketing */}
          <PillarCard
            icon={<TrendingUp className="h-8 w-8" />}
            title="Digital Marketing"
            description="Engage audiences with data-driven strategies and culturally-aware creative."
            cta="Explore Digital Marketing"
            ctaTo="/services/digital-marketing"
            links={[
              { label: 'Influencer Marketing', to: '/services/influencer-marketing' },
              { label: 'Paid Advertising', to: '/services/paid-advertising' },
              { label: 'Social Media Management', to: '/services/social-media' },
              { label: 'Content Creation', to: '/services/content-creation' },
              { label: 'Translation & Cultural Adaptation', to: '/services/translation' },
            ]}
            accent={{
              ring: 'bg-orange-100',
              icon: 'text-orange-600',
              hover: 'bg-orange-50',
              border: 'border-orange-200',
            }}
          />

          {/* Media Planning & Buying */}
          <PillarCard
            icon={<Target className="h-8 w-8" />}
            title="Media Planning & Buying"
            description="Amplify reach across traditional media with precision placement and measurement."
            cta="Explore Media Planning"
            ctaTo="/services/media-planning"
            links={[
              { label: 'TV Advertising', to: '/services/tv-advertising' },
              { label: 'OOH / Outdoor Advertising', to: '/services/ooh-advertising' },
              { label: 'Radio Advertising', to: '/services/radio-advertising' },
              { label: 'Print / Newspaper Advertising', to: '/services/print-advertising' },
              { label: 'Monitoring & Tracking', to: '/services/monitoring' },
              { label: 'Dubbing & Adaptation', to: '/services/dubbing' },
            ]}
            accent={{
              ring: 'bg-orange-100',
              icon: 'text-orange-600',
              hover: 'bg-orange-50',
              border: 'border-orange-200',
            }}
          />
        </div>
      </div>
    </section>
  )
}